
import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Search, UserCheck, CalendarCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  completed: boolean;
  action?: () => void;
  actionLabel?: string;
  icon: React.ElementType;
}

interface OnboardingChecklistProps {
  hasPreferences: boolean;
  hasLeads: boolean;
  hasCareRecipient: boolean;
  hasBookedConsultation: boolean;
}

export function OnboardingChecklist({
  hasPreferences,
  hasLeads,
  hasCareRecipient,
  hasBookedConsultation,
}: OnboardingChecklistProps) {
  const navigate = useNavigate();

  const completedCount = [hasPreferences, hasLeads, hasBookedConsultation].filter(Boolean).length;
  const totalSteps = 3;

  // Don't show if everything is done
  if (completedCount === totalSteps) return null;

  const items: ChecklistItem[] = [
    {
      id: "preferences",
      label: "Set your care preferences",
      description: "Tell us what type of care you're looking for",
      completed: hasPreferences,
      icon: UserCheck,
    },
    {
      id: "explore",
      label: "Save a nursing home",
      description: "Browse and save nursing homes you're interested in",
      completed: hasLeads,
      action: () => navigate("/nursing-homes"),
      actionLabel: "Explore Homes",
      icon: Search,
    },
    {
      id: "consultation",
      label: "Chat with Care Advisor",
      description: "Get free, instant guidance on your care options",
      completed: hasBookedConsultation,
      action: () => navigate("/consultations"),
      actionLabel: "Chat Now",
      icon: CalendarCheck,
    },
  ];

  return (
    <Card className="p-5 border-primary/20 bg-primary/5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-foreground">Get started</h3>
          <p className="text-sm text-muted-foreground">
            {completedCount} of {totalSteps} steps complete
          </p>
        </div>
        <div className="flex items-center gap-1">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-2 w-8 rounded-full transition-colors ${
                i < completedCount ? "bg-primary" : "bg-muted"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {items.map(item => {
          const Icon = item.icon;
          return (
            <div
              key={item.id}
              className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                item.completed ? "bg-background/50" : "bg-background border"
              }`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                item.completed ? "bg-primary/10" : "bg-muted"
              }`}>
                {item.completed ? (
                  <Check className="w-4 h-4 text-primary" />
                ) : (
                  <Icon className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${item.completed ? "text-muted-foreground line-through" : "text-foreground"}`}>
                  {item.label}
                </p>
                {!item.completed && (
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                )}
              </div>
              {!item.completed && item.action && (
                <Button size="sm" variant="outline" onClick={item.action} className="flex-shrink-0">
                  {item.actionLabel}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
